import { useLayoutEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import Scrollbars from '../../../scrollbars';
import Message from './message';
import {
  LEVELING_AGENT_MESSAGE_ROLES,
  LEVELING_AGENT_MESSAGE_TYPES,
} from '../../../../../constants/levelingAgent';
import { richTextContentStyles } from '../../../../../constants/helperCssRules';
import { isArrayEmpty } from '../../../../../utility/helpers';
import {
  getToolCallsForMessage,
  messageContentToReasoning,
  messageContentToText,
} from '../../../../../utility/levelingAgent';

const STREAM_SCROLL_THROTTLE_MS = 100;

const useStyles = makeStyles(theme => ({
  chat: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.primary.bluish9,
    borderRadius: 8,
  },
  messages: {
    padding: theme.spacing(3.5, 3.5, 0),
  },
  message: {
    marginBottom: theme.spacing(4),
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  placeholder: {
    textAlign: 'center',
  },
  scrollY: {
    right: -15,
  },
  messageContent: {
    ...richTextContentStyles(theme),
    fontSize: 16,
    lineHeight: '20px',
    whiteSpace: 'pre-wrap',
    '& p + p': {
      marginTop: theme.spacing(1.5),
    },
    '& ol': {
      margin: 0,
      padding: 0,
      paddingLeft: theme.spacing(4),
      color: 'inherit',
      '& li': {
        marginBottom: theme.spacing(1.5),
        color: 'inherit',
      },
    },
    '& code': {
      fontFamily: 'monospace',
      fontSize: '0.9em',
      backgroundColor: theme.palette.primary.bluish8,
      padding: theme.spacing(0.25, 0.5),
      borderRadius: 4,
    },
    '& pre': {
      margin: theme.spacing(1.5, 0),
      padding: theme.spacing(2),
      backgroundColor: theme.palette.primary.bluish8,
      borderRadius: 4,
      overflow: 'auto',
      '& code': {
        padding: 0,
        backgroundColor: 'transparent',
        fontSize: 'inherit',
      },
    },
  },
  messageAuthor: {
    display: 'block',
    marginBottom: theme.spacing(3),
  },
  userLabel: {
    fontFamily: 'ProximaNova-Bold',
  },
  role: {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.bluish2,
  },
  roleError: {
    color: theme.palette.primary.red1,
  },
  toolCalls: {
    marginTop: theme.spacing(2),
  },
}));

const getMessageType = message => {
  if (typeof message?.getType === 'function') {
    return message.getType();
  }

  if (typeof message?.type === 'string') {
    return message.type.replace(/MessageChunk$/, '').toLowerCase();
  }

  return message?.role;
};

const getMessageRole = message => {
  const messageType = getMessageType(message);

  if (
    messageType === LEVELING_AGENT_MESSAGE_TYPES.HUMAN ||
    messageType === LEVELING_AGENT_MESSAGE_ROLES.USER
  ) {
    return LEVELING_AGENT_MESSAGE_ROLES.USER;
  }
  if (
    messageType === LEVELING_AGENT_MESSAGE_TYPES.AI ||
    messageType === LEVELING_AGENT_MESSAGE_ROLES.ASSISTANT
  ) {
    return LEVELING_AGENT_MESSAGE_ROLES.ASSISTANT;
  }
  return 'unknown';
};

const formatStreamError = error => {
  if (!error) {
    return '';
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && typeof error.message === 'string') {
    return error.message;
  }
  return 'Something went wrong while talking to the leveling agent.';
};

const MessagesPanel = ({
  translations,
  auth,
  messages = [],
  tools = [],
  error = undefined,
  isLoading = false,
  onWriteToolSuccess = undefined,
}) => {
  const classes = useStyles();
  const bottomRef = useRef(null);

  const streamError = formatStreamError(error);
  const isEmpty = isArrayEmpty(messages) && !streamError;
  const lastMessage = messages[messages.length - 1];

  const scrollSignature = useMemo(
    () =>
      [
        messages.length,
        lastMessage?.id ?? '',
        (lastMessage?.tool_calls ?? []).length,
        tools.map(t => `${t.id ?? t.call?.id}:${t.state}`).join('|'),
        isLoading,
      ].join(':'),
    [
      messages.length,
      lastMessage?.id,
      lastMessage?.tool_calls,
      tools,
      isLoading,
    ]
  );

  const streamScrollSignature = useMemo(() => {
    if (!isLoading) {
      return '';
    }

    const lastText = messageContentToText(lastMessage?.content);
    const lastReasoning = messageContentToReasoning(lastMessage?.content)
      .map(block => block.reasoning)
      .join('');

    return `${lastText.length}:${lastReasoning.length}`;
  }, [isLoading, lastMessage?.content]);

  useLayoutEffect(() => {
    if (isEmpty) {
      return;
    }

    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [scrollSignature, isEmpty]);

  useLayoutEffect(() => {
    if (isEmpty || !isLoading || !streamScrollSignature) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      bottomRef.current?.scrollIntoView({ block: 'end' });
    }, STREAM_SCROLL_THROTTLE_MS);

    return () => clearTimeout(timeoutId);
  }, [streamScrollSignature, isEmpty, isLoading]);

  return (
    <div className={classes.chat}>
      {isEmpty ? (
        <div className={classes.emptyState}>
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.placeholder}
          >
            {translations.emptyMessages}
          </Typography>
        </div>
      ) : (
        <Scrollbars verticalScrollClass={classes.scrollY}>
          <div className={classes.messages}>
            {messages.map(message => {
              if (
                getMessageType(message) === LEVELING_AGENT_MESSAGE_TYPES.TOOL
              ) {
                return null;
              }

              const role = getMessageRole(message);
              const text = messageContentToText(message.content).trim();
              const messageTools = getToolCallsForMessage(message, tools);
              const reasoningBlocks = messageContentToReasoning(
                message.content
              );

              if (
                !text &&
                isArrayEmpty(messageTools) &&
                isArrayEmpty(reasoningBlocks)
              ) {
                return null;
              }

              const messageKey =
                message.id ||
                `${role}-${text.slice(0, 32) || messageTools[0]?.call?.id}`;

              return (
                <Message
                  key={messageKey}
                  classes={classes}
                  translations={translations}
                  auth={auth}
                  role={role}
                  text={text}
                  reasoningBlocks={reasoningBlocks}
                  messageTools={messageTools}
                  isStreaming={isLoading && message === lastMessage}
                  onWriteToolSuccess={onWriteToolSuccess}
                />
              );
            })}

            {streamError && (
              <div className={classes.message}>
                <span className={classNames(classes.role, classes.roleError)}>
                  error
                </span>
                <Typography variant="body2" className={classes.messageContent}>
                  {streamError}
                </Typography>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </Scrollbars>
      )}
    </div>
  );
};

MessagesPanel.propTypes = {
  translations: PropTypes.object.isRequired,
  auth: PropTypes.shape({}).isRequired,
  messages: PropTypes.arrayOf(PropTypes.shape({})),
  tools: PropTypes.arrayOf(PropTypes.shape({})),
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isLoading: PropTypes.bool,
  onWriteToolSuccess: PropTypes.func,
};

export default MessagesPanel;
